import React from 'react';
import { Drawer, Typography, Chip, CircularProgress, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { X } from 'lucide-react';

const DRAWER_WIDTH = 400;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '24px 0',
  },
  emptyState: {
    padding: `${theme.spacing(4)}px ${theme.spacing(2)}px`,
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

export interface SideDrawerReportProps {
  open: boolean;
  onClose: () => void;
  /** Icon shown next to the title */
  icon?: React.ReactNode;
  title: string;
  /** Optional status chip next to the title (e.g. score, count) */
  chip?: { label: string; color?: 'primary' | 'secondary' | 'default' };
  /** Optional subtitle line under the header (e.g. "Generated 2h ago") */
  subtitle?: string;
  /** Loading state — renders a spinner row */
  loading?: boolean;
  loadingText?: string;
  /** Empty state — renders when !loading && empty && children not provided */
  empty?: boolean;
  emptyText?: string;
  /** Rendered when not loading and not empty */
  children?: React.ReactNode;
}

/**
 * Generic side-drawer shell used by AI reports (template health, lifecycle analysis, etc.).
 * Handles drawer animation, sticky header, loading/empty states. Caller provides content.
 */
export const SideDrawerReport: React.FC<SideDrawerReportProps> = ({
  open,
  onClose,
  icon,
  title,
  chip,
  subtitle,
  loading,
  loadingText,
  empty,
  emptyText,
  children,
}) => {
  const classes = useStyles();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          {icon}
          <Typography variant="subtitle1" className={classes.title} noWrap>
            {title}
          </Typography>
          {chip && <Chip label={chip.label} size="small" color={chip.color ?? 'primary'} />}
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close drawer">
          <X size={16} />
        </IconButton>
      </div>

      {subtitle && (
        <Typography variant="caption" className={classes.subtitle} component="div">
          {subtitle}
        </Typography>
      )}

      {loading && (
        <div className={classes.loadingRow}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            {loadingText ?? 'Loading...'}
          </Typography>
        </div>
      )}

      {!loading && empty && (
        <div className={classes.emptyState}>
          <Typography variant="body2">{emptyText ?? 'No data available.'}</Typography>
        </div>
      )}

      {!loading && !empty && children}
    </Drawer>
  );
};
